import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface ProductDescriptionConfig {
    title: string;
    content: string;
}

interface ProductDescriptionContextType {
    config: ProductDescriptionConfig;
    updateConfig: (newConfig: Partial<ProductDescriptionConfig>) => void;
    isLoading: boolean;
}

const ProductDescriptionContext = createContext<ProductDescriptionContextType | undefined>(undefined);

interface ProductDescriptionProviderProps {
    children: ReactNode;
}

export const ProductDescriptionProvider: React.FC<ProductDescriptionProviderProps> = ({ children }) => {
    const [config, setConfig] = useState<ProductDescriptionConfig>({
        title: "Description produits",
        content: `Découvrez notre gamme complète de services de plongée sous-marine adaptés à tous les niveaux.
Que vous soyez débutant ou plongeur expérimenté, nous vous proposons des formations certifiantes,
des baptêmes inoubliables et des explorations guidées dans les plus beaux sites de la région.
Nos moniteurs diplômés vous accompagnent dans votre découverte du monde sous-marin avec sécurité et passion.`
    });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setIsLoading(true);
            // Charger depuis le fichier de configuration
            const { PRODUCT_DESCRIPTION_CONFIG } = await import('../config/productDescription');
            setConfig({
                title: PRODUCT_DESCRIPTION_CONFIG.title,
                content: PRODUCT_DESCRIPTION_CONFIG.content
            });
        } catch (error) {
            console.error('Erreur lors du chargement de la configuration:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const updateConfig = (newConfig: Partial<ProductDescriptionConfig>) => {
        setConfig(prev => ({
            ...prev,
            ...newConfig
        }));
    };

    const value: ProductDescriptionContextType = {
        config,
        updateConfig,
        isLoading
    };

    return (
        <ProductDescriptionContext.Provider value={value}>
            {children}
        </ProductDescriptionContext.Provider>
    );
};

export const useProductDescription = (): ProductDescriptionContextType => {
    const context = useContext(ProductDescriptionContext);
    if (context === undefined) {
        throw new Error('useProductDescription must be used within a ProductDescriptionProvider');
    }
    return context;
};
